import React, { FormEvent, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BRAND, HOURS, PHONE_DISPLAY, PHONE_TEL, SITE_URL } from '../constants/brand';
import {
  EMPTY_INTAKE_ANSWERS,
  HOME_TYPE_OPTIONS,
  INTAKE_STEP_COUNT,
  IntakeAnswers,
  IntakeChoice,
  SERVICE_AREA_OPTIONS,
  TIMELINE_OPTIONS,
} from '../data/quoteIntake';
import { ROUTES } from '../enum/routes';
import { isAdsHost } from '../utils/host';

const API_URL = (process.env.REACT_APP_API_URL).replace(/\/$/, '');

const fieldClass =
  'w-full rounded-md border border-black/15 bg-white px-3 py-2.5 text-base text-ink shadow-sm transition focus:border-black/15 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-forest';

function validEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function validPhone(value: string) {
  return value.replace(/\D/g, '').length >= 10;
}

/** Format US phone as (XXX) XXX-XXXX while typing. */
function formatPhoneInput(value: string) {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

type Direction = 1 | -1;

export default function CustomQuoteIntake() {
  const baseId = useId();
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState<Direction>(1);
  const [answers, setAnswers] = useState<IntakeAnswers>(EMPTY_INTAKE_ANSWERS);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [animKey, setAnimKey] = useState(0);
  const advanceTimer = useRef<number | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, [step, success]);

  useEffect(() => {
    return () => {
      if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    };
  }, []);

  const goTo = (next: number, dir: Direction) => {
    setDirection(dir);
    setAnimKey((k) => k + 1);
    setErrors({});
    setStep(next);
  };

  const goBack = () => {
    if (step === 0) return;
    goTo(step - 1, -1);
  };

  const goNext = () => goTo(Math.min(step + 1, INTAKE_STEP_COUNT - 1), 1);

  const pickChoice = (key: keyof IntakeAnswers, value: string) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    advanceTimer.current = window.setTimeout(() => goNext(), 220);
  };

  const setField =
    (key: keyof IntakeAnswers) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setAnswers((prev) => ({ ...prev, [key]: value }));
    };

  const setPhone = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAnswers((prev) => ({ ...prev, phone: formatPhoneInput(e.target.value) }));
  };

  const validateAddress = () => {
    const next: Record<string, string> = {};
    if (!answers.street.trim()) next.street = 'Enter a street address.';
    if (!answers.city.trim()) next.city = 'Enter a city.';
    if (!answers.state.trim()) next.state = 'Enter a state.';
    if (!answers.zip.trim()) next.zip = 'Enter a ZIP code.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const validateContact = () => {
    const next: Record<string, string> = {};
    if (!answers.firstName.trim()) next.firstName = 'Enter your first name.';
    if (!answers.phone.trim()) next.phone = 'Enter a phone number.';
    else if (!validPhone(answers.phone)) next.phone = 'Enter a valid phone number.';
    if (!answers.email.trim()) next.email = 'Enter an email.';
    else if (!validEmail(answers.email)) next.email = 'Enter a valid email.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onAddressContinue = (e: FormEvent) => {
    e.preventDefault();
    if (validateAddress()) goNext();
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateContact() || submitting) return;

    setSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch(`${API_URL}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: answers.firstName.trim(),
          phone: answers.phone,
          email: answers.email.trim(),
          homeType: answers.homeType,
          projectTimeline: answers.projectTimeline,
          serviceArea: answers.serviceArea,
          street: answers.street.trim(),
          city: answers.city.trim(),
          state: answers.state.trim(),
          zip: answers.zip.trim(),
          smsConsent: answers.smsConsent,
        }),
      });

      if (!response.ok) {
        setSubmitError('Something went wrong sending your request. Please try again.');
        return;
      }

      setSuccess(true);
    } catch {
      setSubmitError('Something went wrong sending your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const progress = success ? 100 : ((step + 1) / INTAKE_STEP_COUNT) * 100;
  const animClass = direction === 1 ? 'intake-panel--forward' : 'intake-panel--back';
  const firstName = answers.firstName.trim();
  const adsOnly = isAdsHost();
  const homeHref = adsOnly ? SITE_URL : ROUTES.HOME;
  const termsHref = adsOnly ? `${SITE_URL}${ROUTES.TERMS}` : ROUTES.TERMS;
  const privacyHref = adsOnly ? `${SITE_URL}${ROUTES.PRIVACY}` : ROUTES.PRIVACY;

  return (
    <div className="intake-shell overflow-hidden rounded-lg border border-black/10 bg-white shadow-sm">
      <div className="border-b border-black/5 px-6 py-4 sm:px-8">
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm font-medium text-ink/60">
            {success ? 'Complete' : `Question ${step + 1} of ${INTAKE_STEP_COUNT}`}
          </p>
          {!success && step > 0 ? (
            <button type="button" onClick={goBack} className="text-sm font-semibold text-forest hover:text-forest-hover">
              Back
            </button>
          ) : (
            <span className="text-sm text-transparent" aria-hidden="true">
              Back
            </span>
          )}
        </div>
        <div
          className="mt-3 h-1.5 overflow-hidden rounded-full bg-forest-tint"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
          aria-label="Survey progress"
        >
          <div className="intake-progress h-full rounded-full bg-forest" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {success ? (
        <div className="intake-success intake-panel intake-panel--forward px-6 py-10 text-center sm:px-10 sm:py-14">
          <div className="intake-success-mark mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-forest text-white" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <p className="label-eyebrow mt-7">Request received</p>
          <h2 ref={headingRef} tabIndex={-1} className="font-serif mt-3 text-3xl text-ink outline-none sm:text-4xl">
            {firstName ? `Thanks, ${firstName}.` : "We've got your details."}
          </h2>
          <p className="mx-auto mt-4 max-w-md text-base text-ink/70 sm:text-lg">
            Your free estimate request is in. We'll review it and reach out within one business day.
          </p>

          <div className="mx-auto mt-9 max-w-md rounded-lg border border-black/5 bg-[#f8f9f8] px-5 py-5 text-left sm:px-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-ink/50">What happens next</p>
            <ol className="mt-4 space-y-3.5">
              <li className="flex gap-3">
                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-forest text-xs font-semibold text-white">
                  1
                </span>
                <span className="text-sm text-ink/80 sm:text-base">
                  We review your answers and project details.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-forest text-xs font-semibold text-white">
                  2
                </span>
                <span className="text-sm text-ink/80 sm:text-base">
                  A specialist contacts you to schedule an on-site visit.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-forest text-xs font-semibold text-white">
                  3
                </span>
                <span className="text-sm text-ink/80 sm:text-base">
                  You get a clear estimate with no pressure to move forward.
                </span>
              </li>
            </ol>
          </div>

          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a className="btn-primary w-full sm:w-auto" href={`tel:${PHONE_TEL}`}>
              Call {PHONE_DISPLAY}
            </a>
            {adsOnly ? (
              <a className="btn-secondary w-full sm:w-auto" href={homeHref}>
                Back to home
              </a>
            ) : (
              <Link className="btn-secondary w-full sm:w-auto" to={ROUTES.HOME}>
                Back to home
              </Link>
            )}
          </div>
          <p className="mt-5 text-sm text-ink/50">{HOURS}</p>
        </div>
      ) : (
        <div key={animKey} className={`intake-panel ${animClass} px-6 py-8 sm:px-8 sm:py-10`}>
          {step === 0 && (
            <ChoiceStep
              headingRef={headingRef}
              headingId={`${baseId}-home`}
              question="What best describes your home?"
              options={HOME_TYPE_OPTIONS}
              value={answers.homeType}
              onSelect={(value) => pickChoice('homeType', value)}
              variant="image-grid"
            />
          )}

          {step === 1 && (
            <ChoiceStep
              headingRef={headingRef}
              headingId={`${baseId}-timeline`}
              question="When are you hoping to finish this project?"
              options={TIMELINE_OPTIONS}
              value={answers.projectTimeline}
              onSelect={(value) => pickChoice('projectTimeline', value)}
            />
          )}

          {step === 2 && (
            <ChoiceStep
              headingRef={headingRef}
              headingId={`${baseId}-area`}
              question="Where is the project?"
              options={SERVICE_AREA_OPTIONS}
              value={answers.serviceArea}
              onSelect={(value) => pickChoice('serviceArea', value)}
            />
          )}

          {step === 3 && (
            <form onSubmit={onAddressContinue} noValidate>
              <h2 ref={headingRef} tabIndex={-1} id={`${baseId}-address`} className="font-serif text-3xl text-ink outline-none sm:text-4xl">
                What's the project address?
              </h2>
              <p className="mt-3 text-ink/65">We'll use this for your on-site visit.</p>
              <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="block sm:col-span-2">
                  <span className="mb-1.5 block text-sm font-medium text-ink">
                    Street address<span className="text-forest"> *</span>
                  </span>
                  <input
                    className={`${fieldClass}${errors.street ? ' border-red-500' : ''}`}
                    autoComplete="street-address"
                    value={answers.street}
                    onChange={setField('street')}
                    required
                  />
                  {errors.street ? <span className="mt-1 block text-sm text-red-700">{errors.street}</span> : null}
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink">
                    City<span className="text-forest"> *</span>
                  </span>
                  <input
                    className={`${fieldClass}${errors.city ? ' border-red-500' : ''}`}
                    autoComplete="address-level2"
                    value={answers.city}
                    onChange={setField('city')}
                    required
                  />
                  {errors.city ? <span className="mt-1 block text-sm text-red-700">{errors.city}</span> : null}
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium text-ink">
                      State<span className="text-forest"> *</span>
                    </span>
                    <input
                      className={`${fieldClass}${errors.state ? ' border-red-500' : ''}`}
                      autoComplete="address-level1"
                      value={answers.state}
                      onChange={setField('state')}
                      required
                    />
                    {errors.state ? <span className="mt-1 block text-sm text-red-700">{errors.state}</span> : null}
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium text-ink">
                      ZIP<span className="text-forest"> *</span>
                    </span>
                    <input
                      className={`${fieldClass}${errors.zip ? ' border-red-500' : ''}`}
                      autoComplete="postal-code"
                      inputMode="numeric"
                      value={answers.zip}
                      onChange={setField('zip')}
                      required
                    />
                    {errors.zip ? <span className="mt-1 block text-sm text-red-700">{errors.zip}</span> : null}
                  </label>
                </div>
              </div>
              <button type="submit" className="btn-primary mt-8">
                Continue
              </button>
            </form>
          )}

          {step === 4 && (
            <form onSubmit={onSubmit} noValidate>
              <h2 ref={headingRef} tabIndex={-1} id={`${baseId}-contact`} className="font-serif text-3xl text-ink outline-none sm:text-4xl">
                How can we reach you?
              </h2>
              <p className="mt-3 text-ink/65">We'll follow up within one business day.</p>
              <div className="mt-8 grid grid-cols-1 gap-4">
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink">
                    First name<span className="text-forest"> *</span>
                  </span>
                  <input
                    className={`${fieldClass}${errors.firstName ? ' border-red-500' : ''}`}
                    autoComplete="given-name"
                    value={answers.firstName}
                    onChange={setField('firstName')}
                    required
                  />
                  {errors.firstName ? <span className="mt-1 block text-sm text-red-700">{errors.firstName}</span> : null}
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink">
                    Phone<span className="text-forest"> *</span>
                  </span>
                  <input
                    type="tel"
                    className={`${fieldClass}${errors.phone ? ' border-red-500' : ''}`}
                    autoComplete="tel"
                    placeholder="(512) 555-0123"
                    value={answers.phone}
                    onChange={setPhone}
                    inputMode="tel"
                    maxLength={14}
                    required
                  />
                  {errors.phone ? <span className="mt-1 block text-sm text-red-700">{errors.phone}</span> : null}
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink">
                    Email<span className="text-forest"> *</span>
                  </span>
                  <input
                    type="email"
                    className={`${fieldClass}${errors.email ? ' border-red-500' : ''}`}
                    autoComplete="email"
                    value={answers.email}
                    onChange={setField('email')}
                    required
                  />
                  {errors.email ? <span className="mt-1 block text-sm text-red-700">{errors.email}</span> : null}
                </label>
              </div>
              <label className="mt-6 flex cursor-pointer items-start gap-3 text-sm text-ink/75">
                <input
                  type="checkbox"
                  className="mt-1 accent-forest"
                  checked={answers.smsConsent}
                  onChange={setField('smsConsent')}
                />
                <span>
                  Yes, text me about my estimate. Reply STOP to opt out. Consent is not required to book. See our{' '}
                  {adsOnly ? (
                    <>
                      <a className="underline hover:text-forest" href={termsHref}>
                        Terms
                      </a>{' '}
                      and{' '}
                      <a className="underline hover:text-forest" href={privacyHref}>
                        Privacy Policy
                      </a>
                    </>
                  ) : (
                    <>
                      <Link className="underline hover:text-forest" to={ROUTES.TERMS}>
                        Terms
                      </Link>{' '}
                      and{' '}
                      <Link className="underline hover:text-forest" to={ROUTES.PRIVACY}>
                        Privacy Policy
                      </Link>
                    </>
                  )}
                  .
                </span>
              </label>
              {submitError ? (
                <p className="mt-6 text-sm text-red-700" role="alert">
                  {submitError}
                </p>
              ) : null}
              <button type="submit" className="btn-primary mt-8 w-full sm:w-auto" disabled={submitting}>
                {submitting ? 'Sending…' : 'Get my free estimate'}
              </button>
              <p className="mt-4 text-xs text-ink/55">
                Free estimate. No obligation. Messages from {BRAND} may include appointment details.
              </p>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

type ChoiceStepProps = {
  headingRef: React.RefObject<HTMLHeadingElement | null>;
  headingId: string;
  question: string;
  options: IntakeChoice[];
  value: string;
  onSelect: (value: string) => void;
  variant?: 'list' | 'image-grid';
};

function ChoiceStep({
  headingRef,
  headingId,
  question,
  options,
  value,
  onSelect,
  variant = 'list',
}: ChoiceStepProps) {
  const isImageGrid = variant === 'image-grid';

  return (
    <div>
      <h2 ref={headingRef} tabIndex={-1} id={headingId} className="font-serif text-3xl text-ink outline-none sm:text-4xl">
        {question}
      </h2>
      <div
        className={
          isImageGrid
            ? 'mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4'
            : 'mt-8 space-y-3'
        }
        role="radiogroup"
        aria-labelledby={headingId}
      >
        {options.map((option) => {
          const selected = value === option.value;
          if (isImageGrid && option.imageSrc) {
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => onSelect(option.value)}
                className={`intake-choice intake-choice--image group h-full overflow-hidden rounded-lg border text-left transition ${
                  selected
                    ? 'border-forest bg-white shadow-sm ring-2 ring-forest ring-offset-2'
                    : 'border-black/10 bg-white hover:border-forest/35'
                }`}
              >
                <span className="intake-choice-media">
                  <img
                    src={option.imageSrc}
                    alt={option.imageAlt || option.label}
                    width={640}
                    height={480}
                    loading="eager"
                    decoding="async"
                  />
                </span>
                <span className="intake-choice-footer">
                  <span className={`text-base font-medium ${selected ? 'text-forest' : 'text-ink'}`}>
                    {option.label}
                  </span>
                  <span
                    className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border transition ${
                      selected
                        ? 'border-forest bg-forest text-white'
                        : 'border-black/20 text-transparent group-hover:border-forest/50'
                    }`}
                    aria-hidden="true"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                </span>
              </button>
            );
          }

          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onSelect(option.value)}
              className={`intake-choice group flex w-full items-center justify-between gap-4 rounded-md border px-4 py-4 text-left transition sm:px-5 sm:py-5 ${
                selected
                  ? 'border-forest bg-forest-tint shadow-sm'
                  : 'border-black/10 bg-white hover:border-forest/40 hover:bg-forest-tint/50'
              }`}
            >
              <span className={`text-base font-medium sm:text-lg ${selected ? 'text-forest' : 'text-ink'}`}>
                {option.label}
              </span>
              <span
                className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border transition ${
                  selected ? 'border-forest bg-forest text-white' : 'border-black/20 text-transparent group-hover:border-forest/50'
                }`}
                aria-hidden="true"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
            </button>
          );
        })}
      </div>
      <p className="mt-6 text-sm text-ink/50">Select an option to continue</p>
    </div>
  );
}
